import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  HiOutlineArrowLeft,
  HiBadgeCheck,
  HiOutlineCalendar,
  HiOutlineInformationCircle,
  HiExclamationCircle,
  HiChevronDown,
  HiChevronUp,
} from 'react-icons/hi';
import './PlanDetailsPage.scss';
import {
  PlanBase,
  BASE_PLANS_STRUCTURE,
  ALL_PLANS_BASE_INFO_FOR_DETAILS_PAGE,
  PLAN_DURATIONS,
} from '../../constants';
import logoImage_1 from '../../assets/White PNG 1.png';
import logoImage_2 from '../../assets/Black PNG 1.png';

type ViewState = 'loading' | 'details' | 'purchase' | 'not_found';

interface FeatureDetail {
  title: string;
  description: string;
  example?: string;
  order?: number;
}

const PlanDetailsPage: React.FC = () => {
  const { planId: planIdStr } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const theme = queryParams.get('theme') || 'dark';
  const initialIntent = queryParams.get('intent') || 'details';

  const isAuthenticated = !!localStorage.getItem('token');

  const [currentPlanDetails, setCurrentPlanDetails] = useState<PlanBase | null>(null);
  const [fetchedFeatures, setFetchedFeatures] = useState<FeatureDetail[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState<boolean>(true);
  const [featuresError, setFeaturesError] = useState<string | null>(null);
  const [selectedDurationValue, setSelectedDurationValue] = useState<number>(PLAN_DURATIONS[0].value);
  const [view, setView] = useState<ViewState>('loading');
  const [expandedFeatures, setExpandedFeatures] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const parsedPlanId = planIdStr ? parseInt(planIdStr, 10) : NaN;
    if (isNaN(parsedPlanId)) {
      setView('not_found');
      return;
    }

    const baseDetails = BASE_PLANS_STRUCTURE.find(p => p.id === parsedPlanId);
    const displayInfo = ALL_PLANS_BASE_INFO_FOR_DETAILS_PAGE.find(p => p.id === parsedPlanId);

    if (baseDetails && displayInfo) {
      setCurrentPlanDetails(baseDetails);
      if (initialIntent === 'purchase') {
        if (isAuthenticated) {
          setView('purchase');
        } else {
          navigate('/login', { state: { from: location } });
        }
      } else {
        setView('details');
      }
    } else {
      setView('not_found');
    }

    const fetchFeatureDetails = async () => {
      setFeaturesLoading(true);
      setFeaturesError(null);
      try {
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const plansResponse = await fetch('https://api.neontradepro.com/plan', { headers });
        if (!plansResponse.ok) throw new Error('Failed to fetch plan list.');
        const plans = await plansResponse.json();
        const planData = plans.find((plan: any) => plan.id === parsedPlanId);
        if (!planData || !planData.plan_option_id) {
          throw new Error('Could not find details for this plan.');
        }

        const response = await fetch(`https://api.neontradepro.com/plan/options/${planData.plan_option_id}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch plan features.');

        const responseData = await response.json();
        if (responseData && responseData.data && typeof responseData.data === 'object') {
          const features = Object.values(responseData.data)
            .filter((item): item is FeatureDetail =>
              typeof item === 'object' && item !== null && 'title' in item && 'description' in item
            )
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          setFetchedFeatures(features);
        } else {
          setFetchedFeatures([]);
        }
      } catch (error: any) {
        setFeaturesError(error.message || 'An error occurred while fetching features.');
        setFetchedFeatures([]);
      } finally {
        setFeaturesLoading(false);
      }
    };

    fetchFeatureDetails();
  }, [planIdStr, initialIntent, isAuthenticated, location, navigate]);

  const toggleFeatureExpansion = (key: string) => {
    setExpandedFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const handleStartPurchase = () => {
    if (isAuthenticated) {
      setView('purchase');
    } else {
      navigate('/login', { state: { from: location } });
    }
  };


  const handleContinueToAccount = () => {
    if (!currentPlanDetails) return;
    const selectedDurationLabel = PLAN_DURATIONS.find(d => d.value === selectedDurationValue)?.label || 'نامشخص';
    navigate(
      `/add-account?theme=${theme}&planId=${currentPlanDetails.id}&planName=${encodeURIComponent(
        currentPlanDetails.name
      )}&planDuration=${selectedDurationValue}&periodLabel=${encodeURIComponent(selectedDurationLabel)}`
    );
  };

  const pageHeaderStyle: React.CSSProperties = {
    padding: '0.75rem 2rem',
    borderBottom: '1px solid var(--border-color, #333)',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme === 'dark' ? '#121212' : '#f0f0f0',
    direction: 'rtl',
  };

  const logoInHeaderStyle: React.CSSProperties = {
    height: '30px',
    display: 'block',
  };

  const renderContent = () => {
    switch (view) {
      case 'loading':
        return (
          <div className="details-container error-container">
            <h2>در حال بارگذاری...</h2>
          </div>
        );

      case 'not_found':
        return (
          <div className="details-container error-container">
            <HiExclamationCircle style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '1rem' }} />
            <h2>پلن یافت نشد</h2>
            <Link to={`/?theme=${theme}`} className="details-back-link">
              <HiOutlineArrowLeft /> بازگشت
            </Link>
          </div>
        );

      case 'details':
      case 'purchase':
        if (!currentPlanDetails) return null;
        return (
          <div className="details-container">
            <div className="details-header">
              <h1>پلن {currentPlanDetails.name}</h1>
              <HiBadgeCheck className="header-icon" />
            </div>
            {view === 'details' && (
              <>
                <div className="features-section" style={{ marginBottom: '2.5rem', marginTop: '2rem' }}>
                  <h2
                    style={{
                      fontSize: '1.5rem',
                      textAlign: 'right',
                      marginBottom: '1.5rem',
                      background: 'none',
                      color: 'var(--text-color)',
                    }}
                  >
                    ویژگی‌های پلن
                  </h2>
                  {featuresLoading ? (
                    <p>در حال بارگذاری ویژگی‌ها...</p>
                  ) : featuresError ? (
                    <p style={{ color: 'var(--pdp-error-text)' }}>خطا: {featuresError}</p>
                  ) : (
                    fetchedFeatures.map((detail, detailIndex) => {
                      const featureKey = `feature-${detailIndex}`;
                      const isExpanded = expandedFeatures[featureKey];
                      return (
                        <div key={featureKey} className="feature-detail-card">
                          <div className="feature-card-header" onClick={() => toggleFeatureExpansion(featureKey)}>
                            <h3>{detail.title}</h3>
                            {isExpanded ? <HiChevronUp /> : <HiChevronDown />}
                          </div>
                          {isExpanded && (
                            <div className="feature-card-content">
                              <ReactMarkdown>{detail.description}</ReactMarkdown>
                              {detail.example && <p className="feature-example">{detail.example}</p>}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={handleStartPurchase}
                    className="select-plan-btn-details"
                    style={{ background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))', width:'60%', margin: '2rem auto', padding:'0.75rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                  >
                    شروع فرایند خرید
                  </button>
                </div>
              </>
            )}
            {view === 'purchase' && (
              <>
                <div className="plan-selection-area">
                  <h2
                    style={{
                      fontSize: '1.5rem',
                      textAlign: 'center',
                      marginBottom: '1.5rem',
                      background: 'none',
                      color: 'var(--text-color)',
                    }}
                  >
                    انتخاب مدت زمان اشتراک
                  </h2>
                  <div className="duration-selector-details">
                    <div className="duration-options-details">
                      {PLAN_DURATIONS.map(duration => (
                        <button
                          key={duration.id}
                          className={`duration-option-btn ${selectedDurationValue === duration.value ? 'selected' : ''}`}
                          onClick={() => setSelectedDurationValue(duration.value)}
                        >
                          <HiOutlineCalendar /> {duration.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={handleContinueToAccount} className="select-plan-btn-details">
                    ادامه و انتخاب حساب
                  </button>
                </div>
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                  <button
                    onClick={() => setView('details')}
                    className="details-back-link"
                  >
                    <HiOutlineInformationCircle /> مشاهده ویژگی‌ها
                  </button>
                </div>
              </>
            )}
            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <Link to={`/?theme=${theme}`} className="details-back-link">
                <HiOutlineArrowLeft /> بازگشت به صفحه اصلی
              </Link>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <header style={pageHeaderStyle}>
        <div className="logo">
          <Link to={`/?theme=${theme}`}>
            <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt="لوگوی نئون" style={logoInHeaderStyle} />
          </Link>
        </div>
      </header>
      <div className={`plan-details-page ${theme}`}>{renderContent()}</div>
    </>
  );
};
export default PlanDetailsPage;